<template>
  <div class="tab-content">
    <div class="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div>
        <h2 class="text-2xl font-bold text-gray-900 mb-2 flex items-center">
          <font-awesome-icon icon="graduation-cap" class="mr-3 text-blue-600" />
          Programs
        </h2>
        <p class="text-gray-500">Manage programs for each department</p>
      </div>
      <div class="flex items-center gap-4">
        <select v-model="selectedDepartment"
          class="px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white">
          <option value="">All Departments</option>
          <option v-for="department in allDepartments" :key="department.id" :value="department.id">
            {{ getDepartmentDisplayLabel(department) }}
          </option>
        </select>
        <base-button @click="showModal = true"
          class="bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 px-6 py-3 rounded-lg font-semibold">
          <font-awesome-icon icon="plus" class="mr-2" />
          Add Program
        </base-button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <base-page-spinner type="spin" class-list="h-12"></base-page-spinner>
      <p class="text-gray-500 mt-4">Loading programs...</p>
    </div>

    <!-- Programs List -->
    <div v-else>
      <div v-if="groupedProgramsByDepartment && groupedProgramsByDepartment.length > 0" class="space-y-6">
        <div v-for="department in groupedProgramsByDepartment" :key="department.id"
          class="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-blue-300 transition-all duration-300">
          <div class="flex items-center mb-4">
            <div class="bg-blue-600 rounded-xl p-3 mr-4 shadow-lg">
              <font-awesome-icon icon="sitemap" class="text-white text-xl" />
            </div>
            <div class="flex-1">
              <h3 class="text-xl font-bold text-gray-900">
                {{ getDepartmentDisplayLabel(department) }}
              </h3>
              <p class="text-sm text-gray-500 mt-1">
                {{ department.programs.length }} Program{{ department.programs.length !== 1 ? 's' : '' }}
              </p>
            </div>
          </div>

          <!-- Programs for this department -->
          <div class="ml-12 mt-4 pt-4 border-t border-gray-200">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div v-for="program in department.programs" :key="program.id"
                class="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-blue-50 hover:border-blue-300 transition-all duration-200">
                <div class="flex items-start justify-between mb-3">
                  <div class="flex items-center flex-1">
                    <div class="bg-blue-600 rounded-lg p-2 mr-3">
                      <font-awesome-icon icon="graduation-cap" class="text-white text-sm" />
                    </div>
                    <div class="flex-1">
                      <h4 class="text-lg font-bold text-gray-900">{{ program.name }}</h4>
                    </div>
                  </div>
                  <div class="flex space-x-2">
                    <button @click="editProgram(program)"
                      class="text-blue-600 hover:text-blue-800 hover:bg-blue-100 p-2 rounded-lg transition-all duration-200">
                      <font-awesome-icon icon="edit" class="text-sm" />
                    </button>
                    <button @click="deleteProgram(program.id)"
                      class="text-red-600 hover:text-red-800 hover:bg-red-100 p-2 rounded-lg transition-all duration-200">
                      <font-awesome-icon icon="trash" class="text-sm" />
                    </button>
                  </div>
                </div>
                <div class="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                  <span :class="[
                    'px-2 py-1 rounded-full text-xs font-semibold',
                    program.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  ]">
                    {{ program.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-12 bg-gray-50 rounded-xl">
        <font-awesome-icon icon="graduation-cap" class="text-4xl text-gray-400 mb-4" />
        <p class="text-gray-500">No programs found</p>
        <p class="text-sm text-gray-400 mt-2" v-if="!loading">
          {{ getEmptyProgramsMessage() }}
        </p>
      </div>
    </div>

    <!-- Program Modal -->
    <base-modal v-if="showModal" :title="form.id ? 'Edit Program' : 'Add Program'" @close="closeModal">
      <div class="p-8">
        <div class="mb-6">
          <label class="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <font-awesome-icon icon="sitemap" class="mr-2 text-blue-600" />
            Department
          </label>
          <select v-model="form.program_department_id"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white"
            :disabled="!!form.id">
            <option value="">Select Department</option>
            <option v-for="department in allDepartments" :key="department.id" :value="department.id">
              {{ getDepartmentDisplayLabel(department) }}
            </option>
          </select>
        </div>
        <div class="mb-6">
          <label class="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <font-awesome-icon icon="graduation-cap" class="mr-2 text-blue-600" />
            Program Name
          </label>
          <input v-model="form.name" type="text" placeholder="e.g., Nursing"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white" />
        </div>
        <div class="mb-6">
          <label class="flex items-center space-x-3 cursor-pointer">
            <input v-model="form.is_active" type="checkbox"
              class="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
            <span class="text-sm font-semibold text-gray-700">Active</span>
          </label>
        </div>
        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <base-button @click="closeModal"
            class="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all duration-200">
            Cancel
          </base-button>
          <base-button @click="form.id ? updateProgram() : saveProgram()"
            class="bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 px-6 py-3 rounded-lg font-semibold">
            {{ form.id ? 'Update' : 'Save' }}
          </base-button>
        </div>
      </div>
    </base-modal>
  </div>
</template>

<script>
import { gql } from 'graphql-tag';
import { getDisplayEntityCode, getEntityOrderIndex, getProgramDisplayName } from '../utils';

export default {
  name: 'ProgramsTab',
  props: {
    allDepartments: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      allPrograms: [],
      selectedDepartment: '',
      showModal: false,
      form: {
        id: null,
        program_department_id: '',
        name: '',
        is_active: true
      },
      loadingData: false
    };
  },
  computed: {
    filteredPrograms() {
      if (!this.allPrograms || !Array.isArray(this.allPrograms)) {
        return [];
      }

      let programs = [...this.allPrograms];

      if (this.selectedDepartment) {
        programs = programs.filter(p => p.program_department_id === this.selectedDepartment);
      }

      return programs.sort((a, b) => {
        const deptA = a.program_department && a.program_department.name ? a.program_department.name : '';
        const deptB = b.program_department && b.program_department.name ? b.program_department.name : '';
        const deptCompare = deptA.localeCompare(deptB);

        if (deptCompare !== 0) {
          return deptCompare;
        }

        return (a.name || '').localeCompare(b.name || '');
      });
    },
    groupedProgramsByDepartment() {
      if (!this.filteredPrograms || this.filteredPrograms.length === 0) {
        return [];
      }

      const grouped = {};

      this.filteredPrograms.forEach(program => {
        if (!program.program_department) return;

        const deptId = program.program_department_id;
        if (!grouped[deptId]) {
          grouped[deptId] = {
            id: program.program_department.id,
            entity: program.program_department.entity,
            campus: program.program_department.campus,
            name: program.program_department.name,
            programs: []
          };
        }
        grouped[deptId].programs.push(program);
      });

      return Object.values(grouped).sort((a, b) => {
        const indexA = getEntityOrderIndex(a.entity);
        const indexB = getEntityOrderIndex(b.entity);

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        return a.name.localeCompare(b.name);
      });
    }
  },
  methods: {
    getDisplayEntityCode,
    getProgramDisplayName,
    getDepartmentDisplayLabel(department) {
      if (!department) return '';
      const entity = getDisplayEntityCode(department.entity);
      const campus = department.campus || '-';
      const name = department.name || '';
      return `${entity} - ${campus} - ${name}`;
    },
    getEmptyProgramsMessage() {
      return this.selectedDepartment
        ? 'No programs found for the selected department.'
        : 'No programs have been added yet.';
    },
    async loadPrograms() {
      this.loadingData = true;
      try {
        const response = await this.$apollo.query({
          query: gql`
            query GetPrograms($program_department_id: ID, $entity: String, $is_active: Boolean) {
              programs(program_department_id: $program_department_id, entity: $entity, is_active: $is_active) {
                error
                error_code
                message
                data {
                  id
                  program_department_id
                  name
                  is_active
                  created_at
                  updated_at
                  program_department {
                    id
                    entity
                    campus
                    prg_dpt
                    name
                    is_active
                  }
                }
              }
            }
          `,
          variables: {
            program_department_id: null,
            entity: null,
            is_active: null
          },
          fetchPolicy: 'network-only'
        });

        if (response.data.programs.error) {
          console.error('Failed to load programs:', response.data.programs.message);
          this.allPrograms = [];
          return;
        }

        this.allPrograms = response.data.programs.data || [];
      } catch (error) {
        console.error('Failed to load programs:', error);
        this.allPrograms = [];
      } finally {
        this.loadingData = false;
      }
    },
    closeModal() {
      this.showModal = false;
      this.form = {
        id: null,
        program_department_id: '',
        name: '',
        is_active: true
      };
    },
    async saveProgram() {
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation CreateProgram($input: CreateProgramInput!) {
              createProgram(input: $input) {
                error
                error_code
                message
                data {
                  id
                  program_department_id
                  name
                  is_active
                  program_department {
                    id
                    entity
                    prg_dpt
                    name
                  }
                }
              }
            }
          `,
          variables: {
            input: {
              program_department_id: this.form.program_department_id,
              name: this.form.name,
              is_active: this.form.is_active !== undefined ? this.form.is_active : true
            }
          }
        });

        if (response.data.createProgram.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.data.createProgram.message
          });
          return;
        }

        const Toast = this.$swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener('mouseenter', this.$swal.stopTimer);
            toast.addEventListener('mouseleave', this.$swal.resumeTimer);
          }
        });
        Toast.fire({
          icon: 'success',
          title: 'Program created successfully.'
        });

        this.closeModal();
        await this.$nextTick();
        this.loadPrograms();
        this.$emit('refresh', 'programs');
      } catch (error) {
        console.error('Failed to save program:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to save program. Please try again.'
        });
      }
    },
    async updateProgram() {
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation UpdateProgram($input: UpdateProgramInput!) {
              updateProgram(input: $input) {
                error
                error_code
                message
                data {
                  id
                  program_department_id
                  name
                  is_active
                  program_department {
                    id
                    entity
                    prg_dpt
                    name
                  }
                }
              }
            }
          `,
          variables: {
            input: {
              id: this.form.id,
              name: this.form.name,
              is_active: this.form.is_active
            }
          }
        });

        if (response.data.updateProgram.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.data.updateProgram.message
          });
          return;
        }

        const Toast = this.$swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener('mouseenter', this.$swal.stopTimer);
            toast.addEventListener('mouseleave', this.$swal.resumeTimer);
          }
        });
        Toast.fire({
          icon: 'success',
          title: 'Program updated successfully.'
        });

        this.closeModal();
        await this.$nextTick();
        this.loadPrograms();
        this.$emit('refresh', 'programs');
      } catch (error) {
        console.error('Failed to update program:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to update program. Please try again.'
        });
      }
    },
    editProgram(program) {
      this.form = {
        id: program.id,
        program_department_id: program.program_department_id,
        name: program.name,
        is_active: program.is_active
      };
      this.showModal = true;
    },
    async deleteProgram(id) {
      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: 'This will permanently delete the program.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation DeleteProgram($id: ID!) {
              deleteProgram(id: $id) {
                error
                error_code
                message
              }
            }
          `,
          variables: {
            id: id
          }
        });

        if (response.data.deleteProgram.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.data.deleteProgram.message
          });
          return;
        }

        const Toast = this.$swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener('mouseenter', this.$swal.stopTimer);
            toast.addEventListener('mouseleave', this.$swal.resumeTimer);
          }
        });
        Toast.fire({
          icon: 'success',
          title: 'Program has been deleted.'
        });

        await this.$nextTick();
        this.loadPrograms();
        this.$emit('refresh', 'programs');
      } catch (error) {
        console.error('Failed to delete program:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete program. Please try again.'
        });
      }
    }
  },
  mounted() {
    this.loadPrograms();
  }
};
</script>
